import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Plus, Upload, Download, FileText, AlertCircle, Trash2, DollarSign, Loader2 } from 'lucide-react';
import {
  BulkMvrItem,
  BulkMvrPricingResponse,
  StateCapabilities,
  MvrRecordType,
  fetchBulkMvrPricing,
  parseMvrItemsFromCsv,
  downloadMvrCsvTemplate,
  stateNames
} from '../services/mvrService';
import { getErrorMessage } from '../services/apiService';

interface BulkMVRInputProps {
  onSubmit: (items: BulkMvrItem[]) => void;
  disabled?: boolean;
  maxItems?: number;
  supportedStates: StateCapabilities[];
  showPricing?: boolean;
}

const BulkMVRInput: React.FC<BulkMVRInputProps> = ({
  onSubmit,
  disabled = false,
  maxItems = 50,
  supportedStates,
  showPricing = false,
}) => {
  const [inputMode, setInputMode] = useState<'manual' | 'csv'>('manual');
  const [manualItems, setManualItems] = useState<BulkMvrItem[]>([
    { vin: '', stateCode: '', recordType: 'both', referenceNote: '' }
  ]);
  const [csvItems, setCsvItems] = useState<BulkMvrItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string>('');
  const [pricing, setPricing] = useState<BulkMvrPricingResponse | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pricingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Validate VIN format (basic check)
  const isValidVin = (vin: string): boolean => {
    if (!vin) return false;
    const cleaned = vin.toUpperCase().trim();
    // Check length and invalid characters
    if (cleaned.length < 5 || cleaned.length > 17) return false;
    if (/[IOQ]/.test(cleaned)) return false;
    return true;
  };

  // Validate state code
  const isValidState = useCallback((stateCode: string): boolean => {
    if (!stateCode) return false;
    return supportedStates.some(s => {
      const stateName = stateNames[stateCode];
      return stateName && s.state === stateName;
    });
  }, [supportedStates]);

  // Check if a state has separate Title/Registration pricing
  const stateHasSeparatePricing = useCallback((stateCode: string): boolean => {
    if (!stateCode) return true; // Default to showing dropdown
    const stateName = stateNames[stateCode];
    if (!stateName) return true;
    const stateCapabilities = supportedStates.find(s => s.state === stateName);
    // Default to true if capability not found (show dropdown)
    return stateCapabilities?.hasSeparatePricing !== false;
  }, [supportedStates]);

  // Check if a state uses combined search (single portal call for both record types).
  // A combined-search state with separate title/registration prices (SC) still lets the
  // customer pick one record type, so it is not treated as combined here.
  const stateIsCombinedSearch = useCallback((stateCode: string): boolean => {
    if (!stateCode) return false;
    const stateName = stateNames[stateCode];
    if (!stateName) return false;
    const stateCapabilities = supportedStates.find(s => s.state === stateName);
    return stateCapabilities?.supportsCombinedSearch === true && stateCapabilities?.hasSeparatePricing !== true;
  }, [supportedStates]);

  // Get current items based on input mode
  const getCurrentItems = useCallback((): BulkMvrItem[] => {
    if (inputMode === 'manual') {
      return manualItems
        .filter(item => item.vin.trim() && item.stateCode.trim())
        .map(item => ({
          vin: item.vin.toUpperCase().trim(),
          stateCode: item.stateCode.toUpperCase().trim(),
          recordType: item.recordType,
          referenceNote: item.referenceNote?.trim() || undefined,
        }));
    }
    return csvItems;
  }, [inputMode, manualItems, csvItems]);

  // Get valid items count
  const validItemsCount = getCurrentItems().filter(
    item => isValidVin(item.vin) && isValidState(item.stateCode)
  ).length;

  // Fetch pricing with debounce
  useEffect(() => {
    if (!showPricing) return;

    const items = getCurrentItems();
    const validItems = items.filter(item => isValidVin(item.vin) && isValidState(item.stateCode));

    if (validItems.length === 0) {
      setPricing(null);
      return;
    }

    // Clear existing timeout
    if (pricingTimeoutRef.current) {
      clearTimeout(pricingTimeoutRef.current);
    }

    // Set new timeout for debounced fetch
    pricingTimeoutRef.current = setTimeout(async () => {
      setPricingLoading(true);
      try {
        const result = await fetchBulkMvrPricing(validItems);
        setPricing(result);
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
        setPricing(null);
      } finally {
        setPricingLoading(false);
      }
    }, 500);

    return () => {
      if (pricingTimeoutRef.current) {
        clearTimeout(pricingTimeoutRef.current);
      }
    };
  }, [inputMode, manualItems, csvItems, showPricing, getCurrentItems, isValidState]);

  // Handle manual item change
  const handleManualItemChange = (
    index: number,
    field: keyof BulkMvrItem,
    value: string
  ) => {
    const newItems = [...manualItems];
    newItems[index] = { ...newItems[index], [field]: value };

    // If state changed to a single-price or combined-search state, auto-set recordType to 'both'
    if (field === 'stateCode' && (!stateHasSeparatePricing(value) || stateIsCombinedSearch(value))) {
      newItems[index].recordType = 'both';
    }

    setManualItems(newItems);
    setError('');
  };

  // Add new manual item row
  const addManualRow = () => {
    if (manualItems.length >= maxItems) {
      setError(`Maximum ${maxItems} items allowed`);
      return;
    }
    setManualItems([...manualItems, { vin: '', stateCode: '', recordType: 'both', referenceNote: '' }]);
  };

  // Remove manual item row
  const removeManualRow = (index: number) => {
    if (manualItems.length === 1) {
      setManualItems([{ vin: '', stateCode: '', recordType: 'both', referenceNote: '' }]);
    } else {
      setManualItems(manualItems.filter((_, i) => i !== index));
    }
    setError('');
  };

  // Process uploaded file
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setError('Please upload a CSV or TXT file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;

      try {
        const items = parseMvrItemsFromCsv(content);

        if (items.length === 0) {
          setError('No valid items found in the file');
          return;
        }

        if (items.length > maxItems) {
          setError(`File contains ${items.length} items. Maximum ${maxItems} allowed. Only first ${maxItems} will be used.`);
          setCsvItems(items.slice(0, maxItems));
        } else {
          setError('');
          setCsvItems(items);
        }
      } catch (err) {
        setError(getErrorMessage(err));
        setCsvItems([]);
      }
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  }, [maxItems]);

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  }, [processFile]);

  // Handle file select
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Clear all items
  const handleClear = () => {
    if (inputMode === 'manual') {
      setManualItems([{ vin: '', stateCode: '', recordType: 'both', referenceNote: '' }]);
    } else {
      setCsvItems([]);
    }
    setError('');
    setPricing(null);
  };

  // Submit items
  const handleSubmit = () => {
    const items = getCurrentItems();
    const validItems = items.filter(item => isValidVin(item.vin) && isValidState(item.stateCode));

    if (validItems.length === 0) {
      setError('Please enter at least one valid VIN with a supported state');
      return;
    }

    // Check for duplicates (VIN + State + RecordType)
    const seen = new Set<string>();
    const duplicates: string[] = [];
    validItems.forEach(item => {
      const key = `${item.vin}-${item.stateCode}-${item.recordType}`;
      if (seen.has(key)) {
        duplicates.push(`${item.vin} (${item.stateCode} - ${item.recordType})`);
      } else {
        seen.add(key);
      }
    });

    if (duplicates.length > 0) {
      setError(`Duplicate items detected and will be removed: ${duplicates.join(', ')}`);
    }

    // Remove duplicates and submit
    const uniqueItems = validItems.filter((item, index, self) =>
      index === self.findIndex(i =>
        i.vin === item.vin && i.stateCode === item.stateCode && i.recordType === item.recordType
      )
    );

    onSubmit(uniqueItems);
  };

  // Get record type display label
  const getRecordTypeLabel = (recordType: MvrRecordType): string => {
    const labels: Record<MvrRecordType, string> = {
      'title': 'Title',
      'registration': 'Registration',
      'both': 'Both',
    };
    return labels[recordType] || recordType;
  };

  return (
    <div className="space-y-4">
      {/* Mode Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setInputMode('manual')}
          disabled={disabled}
          className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 ${
            inputMode === 'manual'
              ? 'bg-[#c41e3a]/10 text-[#c41e3a] border border-[#c41e3a]/30'
              : 'bg-white/60 text-[#64748b] border border-black/[0.08] hover:bg-white hover:text-[#0f172a]'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <FileText className="w-4 h-4" />
          Manual Entry
        </button>
        <button
          onClick={() => setInputMode('csv')}
          disabled={disabled}
          className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 ${
            inputMode === 'csv'
              ? 'bg-[#c41e3a]/10 text-[#c41e3a] border border-[#c41e3a]/30'
              : 'bg-white/60 text-[#64748b] border border-black/[0.08] hover:bg-white hover:text-[#0f172a]'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <Upload className="w-4 h-4" />
          CSV Upload
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-lg text-[#dc2626] text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Manual Entry Mode */}
      {inputMode === 'manual' && (
        <div className="space-y-3">
          <div className="text-xs text-[#64748b]">
            Enter VINs with state and record type. Maximum {maxItems} items.
          </div>

          {/* Header row */}
          <div className="grid grid-cols-12 gap-2 text-xs font-medium text-[#64748b] px-4">
            <div className="col-span-4">VIN</div>
            <div className="col-span-2">State</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-3">Reference Note (Optional)</div>
            <div className="col-span-1"></div>
          </div>

          {/* Input rows */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto -mx-4 px-4 py-1">
            {manualItems.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="text"
                  value={item.vin}
                  onChange={(e) => handleManualItemChange(index, 'vin', e.target.value.toUpperCase())}
                  disabled={disabled}
                  placeholder="Enter VIN..."
                  className={`col-span-4 px-3 py-2 bg-white border rounded-lg text-[#0f172a] text-sm font-mono placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] focus:border-transparent transition-all disabled:opacity-50 ${
                    item.vin && !isValidVin(item.vin)
                      ? 'border-[#dc2626]/50'
                      : 'border-black/[0.08]'
                  }`}
                  maxLength={17}
                />
                <select
                  value={item.stateCode}
                  onChange={(e) => handleManualItemChange(index, 'stateCode', e.target.value)}
                  disabled={disabled}
                  className={`col-span-2 px-3 py-2 bg-white border rounded-lg text-[#0f172a] text-sm focus:outline-none focus:ring-2 focus:ring-[#c41e3a] focus:border-transparent transition-all disabled:opacity-50 ${
                    item.stateCode && !isValidState(item.stateCode)
                      ? 'border-[#dc2626]/50'
                      : 'border-black/[0.08]'
                  }`}
                >
                  <option value="">Select...</option>
                  {supportedStates
                    .map(s => ({
                      code: Object.keys(stateNames).find(code => stateNames[code] === s.state),
                      name: s.state
                    }))
                    .filter(s => s.code)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(s => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                </select>
                {stateHasSeparatePricing(item.stateCode) && !stateIsCombinedSearch(item.stateCode) ? (
                  <select
                    value={item.recordType}
                    onChange={(e) => handleManualItemChange(index, 'recordType', e.target.value as MvrRecordType)}
                    disabled={disabled}
                    className="col-span-2 px-3 py-2 bg-white border border-black/[0.08] rounded-lg text-[#0f172a] text-sm focus:outline-none focus:ring-2 focus:ring-[#c41e3a] focus:border-transparent transition-all disabled:opacity-50"
                  >
                    <option value="both">Both</option>
                    <option value="title">Title</option>
                    <option value="registration">Registration</option>
                  </select>
                ) : (
                  <div className="col-span-2 px-3 py-2 bg-gray-50 border border-black/[0.08] rounded-lg text-[#64748b] text-sm">
                    Both
                  </div>
                )}
                <input
                  type="text"
                  value={item.referenceNote || ''}
                  onChange={(e) => handleManualItemChange(index, 'referenceNote', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g., Customer name..."
                  className="col-span-3 px-3 py-2 bg-white border border-black/[0.08] rounded-lg text-[#0f172a] text-sm placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] focus:border-transparent transition-all disabled:opacity-50"
                />
                <button
                  onClick={() => removeManualRow(index)}
                  disabled={disabled}
                  className="col-span-1 p-2 text-[#94a3b8] hover:text-[#dc2626] transition-colors disabled:opacity-50"
                  title="Remove row"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Row Button */}
          <button
            onClick={addManualRow}
            disabled={disabled || manualItems.length >= maxItems}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[#64748b] hover:text-[#c41e3a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Add Row
          </button>
        </div>
      )}

      {/* CSV Upload Mode */}
      {inputMode === 'csv' && (
        <div className="space-y-4">
          {/* Download Template Link */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#64748b]">
              Upload a CSV file with VIN, State, RecordType, and ReferenceNote columns.
            </span>
            <button
              onClick={downloadMvrCsvTemplate}
              disabled={disabled}
              className="flex items-center gap-1 text-xs text-[#c41e3a] hover:text-[#a01830] font-medium transition-colors disabled:opacity-50"
            >
              <Download className="w-3 h-3" />
              Download Template
            </button>
          </div>

          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onClick={() => !disabled && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              dragActive
                ? 'border-[#c41e3a] bg-[#c41e3a]/5'
                : 'border-black/[0.15] hover:border-[#c41e3a]/50 hover:bg-white/60'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Upload className="w-10 h-10 mx-auto mb-3 text-[#94a3b8]" />
            <p className="text-sm text-[#0f172a] mb-1">
              Drag & drop your CSV file here
            </p>
            <p className="text-xs text-[#64748b]">
              or click to browse
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileSelect}
            disabled={disabled}
            className="hidden"
          />

          {/* CSV Preview */}
          {csvItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#0f172a]">
                  {csvItems.length} item(s) loaded
                </span>
                <button
                  onClick={() => setCsvItems([])}
                  disabled={disabled}
                  className="flex items-center gap-1 text-xs text-[#64748b] hover:text-[#dc2626] transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear
                </button>
              </div>

              <div className="max-h-[200px] overflow-y-auto bg-white rounded-lg border border-black/[0.08]">
                <table className="w-full text-sm">
                  <thead className="bg-[#f8fafc] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-[#64748b]">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-[#64748b]">VIN</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-[#64748b]">State</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-[#64748b]">Type</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-[#64748b]">Reference Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvItems.map((item, index) => (
                      <tr key={index} className="border-t border-black/[0.05]">
                        <td className="px-3 py-2 text-[#64748b]">{index + 1}</td>
                        <td className={`px-3 py-2 font-mono text-xs ${
                          isValidVin(item.vin) ? 'text-[#0f172a]' : 'text-[#dc2626]'
                        }`}>
                          {item.vin}
                        </td>
                        <td className={`px-3 py-2 text-xs ${
                          isValidState(item.stateCode) ? 'text-[#0f172a]' : 'text-[#dc2626]'
                        }`}>
                          {stateNames[item.stateCode] || item.stateCode}
                        </td>
                        <td className="px-3 py-2 text-[#64748b] text-xs">
                          {getRecordTypeLabel(item.recordType)}
                        </td>
                        <td className="px-3 py-2 text-[#64748b] text-xs">{item.referenceNote || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pricing Summary */}
      {showPricing && validItemsCount > 0 && (
        <div className="glass-card-strong p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-[#0f172a]">
            <DollarSign className="w-4 h-4 text-[#c41e3a]" />
            Pricing Breakdown
            {pricingLoading && <Loader2 className="w-4 h-4 animate-spin text-[#64748b]" />}
          </div>

          {pricingLoading ? (
            <div className="flex items-center justify-center py-4 text-sm text-[#64748b]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Calculating pricing...
            </div>
          ) : pricing && pricing.breakdown.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-black/[0.08]">
              <table className="w-full text-sm">
                <thead className="bg-[#f8fafc]">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-[#64748b]">State</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-[#64748b]">Type</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-[#64748b]">Qty</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-[#64748b]">Unit</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-[#64748b]">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {pricing.breakdown.map((group, index) => (
                    <tr key={index} className="border-t border-black/[0.05]">
                      <td className="px-3 py-2 text-[#0f172a]">{group.stateName}</td>
                      <td className="px-3 py-2 text-[#64748b] capitalize">{group.recordType}</td>
                      <td className="px-3 py-2 text-right text-[#0f172a]">{group.count}</td>
                      <td className="px-3 py-2 text-right text-[#64748b]">
                        ${group.unitCost.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right text-[#0f172a] font-medium">
                        ${group.subtotal.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-black/[0.08] bg-[#f8fafc] font-semibold">
                    <td colSpan={4} className="px-3 py-2 text-[#0f172a]">Total</td>
                    <td className="px-3 py-2 text-right text-[#c41e3a]">
                      ${pricing.totalEstimatedCost.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-[#64748b] text-center py-2">
              Unable to calculate pricing
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-black/[0.06]">
        <div className="flex items-center gap-3">
          <div className="text-sm text-[#64748b]">
            {validItemsCount > 0 ? (
              <span className="text-[#16a34a] font-medium">{validItemsCount} valid item(s) ready</span>
            ) : (
              <span>No items entered</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleClear}
            disabled={disabled}
            className="px-4 py-2 text-sm text-[#64748b] hover:text-[#0f172a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear
          </button>
          <button
            onClick={handleSubmit}
            disabled={disabled || validItemsCount === 0}
            className="btn-primary px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Search {validItemsCount > 0 ? `(${validItemsCount})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkMVRInput;
